import React, { useContext, useEffect, useState } from 'react';
import styles from './story-selection.module.css';
import { ShareLinkContext } from '../share/ShareLinkContext';
import { WeekReflectionContext } from './WeekReflectionContext';
import { ComponentMap } from './StoryComponentMap';
import FlowentryDetailView from '../myCheckins/FlowentryDetailView';

function StorySelection({ bgColor, onNext }) {
    const { weekReviewManager } = useContext(WeekReflectionContext);
    const [stories] = useState(weekReviewManager.stories || []);
    const [showPeakValley, setShowPeakValley] = useState(false);
    const [selectedStories, setSelectedStories] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const { setStorySelection } = useContext(ShareLinkContext);

    //#region story/comment selection
    const handleSelectAll = (e) => {
        e.stopPropagation();

        const checked = !selectAll;
        setSelectAll(checked);

        if (checked) {
            const allStories = weekReviewManager.stories.map((storyData, index) => {
                let componentName = storyData.component;
                if (componentName.includes('recurringCauses') && storyData.index !== undefined) {
                    componentName = componentName + index;
                }
                const isCommentIncluded = storyData.data?.comments && storyData.data.comments.length > 0 ? true : false;
                // If the story has comments, we include it in the selection
                let selectedItem = { index, componentName, isCommentIncluded };
                if (storyData.component === 'peakValley') {
                    selectedItem.peakValleySelection = storyData.data?.sortedPeakAndValleyFlows?.map((_, flowIndex) => flowIndex) || [];
                }
                return selectedItem;
            });

            setSelectedStories(allStories);
        }
        else {
            setSelectedStories([]);
        }
    }

    const handleStorySelection = (e, index, componentName, componentIndex, hasComment) => {
        e.stopPropagation();

        if (componentName.includes('recurringCauses') && componentIndex !== undefined) {
            componentName = componentName + componentIndex;
        }

        setSelectedStories((prevSelected) => {
            const isSelected = prevSelected.some(selected => selected.index === index);
            if (isSelected) {
                return prevSelected.filter(element => element.index !== index); // Deselect
            } else {
                const newItem = { index, componentName, isCommentIncluded: hasComment ? true : false };
                if (componentName === 'peakValley') {
                    newItem.peakValleySelection = stories[peakValleyStoryIndex]?.data?.sortedPeakAndValleyFlows?.map((_, flowIndex) => flowIndex) || [];
                }
                return [...prevSelected, newItem]; // Select
            }
        });
    }

    const handleChildClick = (e, index) => {
        e.stopPropagation();

        setSelectedStories((prevSelected) => {
            const isAlreadyIncluded = prevSelected.some(selected => selected.index === index && selected.isCommentIncluded);
            if (isAlreadyIncluded) {
                // uncheck by setting the isCommentIncluded to false
                // this map just modifies the element which matches condition, and returns the other elements as is
                return prevSelected.map(selected =>
                    selected.index === index ? { ...selected, isCommentIncluded: false } : selected
                );
            } else {
                // set the isCommentIncluded to true
                return prevSelected.map(selected =>
                    selected.index === index ? { ...selected, isCommentIncluded: true } : selected
                );
            }
        });
    }
    //#endregion

    //#region peak valley flow selection
    const peakValleyStoryIndex = stories.findIndex(story => story.component === 'peakValley');

    useEffect(() => {
        const isPeakValleyStorySelected = selectedStories.some(selection => selection.index === peakValleyStoryIndex);
        if (isPeakValleyStorySelected) {
            setShowPeakValley(true);
        }
        else {
            setShowPeakValley(false);
        }
    }, [selectedStories])

    const handlePeakValleyClick = (e, flowIndex) => {
        e.stopPropagation();

        const peakValley = selectedStories.find(selection => selection.index === peakValleyStoryIndex);
        if (!peakValley) {
            return;
        }
        setSelectedStories((prevSelected) => {
            const isFlowAlreadySelected = peakValley.peakValleySelection && peakValley.peakValleySelection.includes(flowIndex);
            if (isFlowAlreadySelected) {
                // uncheck by removing the flowIndex from the peakValleySelection
                const peakValleySelection = peakValley.peakValleySelection?.filter(index => index !== flowIndex);
                // this map just modifies the element which matches condition, and returns the other elements as is
                return prevSelected.map(selected =>
                    selected.index === peakValleyStoryIndex ? { ...selected, peakValleySelection } : selected
                );
            } else {
                const newPeakValleySelection = peakValley.peakValleySelection ? [...peakValley.peakValleySelection, flowIndex] : [flowIndex];
                return prevSelected.map(selected =>
                    selected.index === peakValleyStoryIndex ? { ...selected, peakValleySelection: newPeakValleySelection } : selected
                );
            }
        });

    }

    const isFlowSelected = (index) => {
        const peakValley = selectedStories.find(selection => selection.index === peakValleyStoryIndex);
        return peakValley && peakValley.peakValleySelection && peakValley.peakValleySelection.includes(index);
    }

    const getPeakAndValleyForSelection = () => {
        const flows = stories[peakValleyStoryIndex]?.data?.sortedPeakAndValleyFlows || [];

        return flows.map((flow, index) =>
            <div className={styles.storyWrapper} key={index + 100}>
                <div className={styles.littleStory} style={{ background: bgColor }}>
                    <div style={{ overflowY: "auto" }} key={flow.entry._id}>
                        <FlowentryDetailView key={flow.entry._id} flowEntry={flow.entry} />
                    </div>
                </div>

                <div className={styles.checkboxOverlay} onClick={(e) => handlePeakValleyClick(e, index)}>
                    <div className={styles.parentCheckboxArea}>
                        <input type="checkbox"
                            checked={isFlowSelected(index)}
                            readOnly
                        />
                    </div>
                </div>
            </div>
        )
    }
    //#endregion

    const saveSelection = (e) => {
        e.stopPropagation();

        // set the selection after sorting it by index in the share link context
        const sortedSelection = selectedStories.sort((a, b) => a.index - b.index);
        setStorySelection(sortedSelection);
        onNext();
    }

    return (
        <div className={styles.selectionModal}>
            <div className={styles.headerRow}>
                <h3 className={styles.selectionTitle}>Select Stories to Share</h3>
                <div className={styles.selectAllDiv} onClick={(e) => handleSelectAll(e)}>
                    <input type="checkbox"
                        checked={selectAll}
                        className={styles.selectAllCheckbox}
                        readOnly
                    />
                    <span className={styles.selectAllText}>Select All</span>
                </div>
            </div>
            <div className={styles.scrollableContent}>
                {stories?.map((storyData, index) => {
                    const ComponentToRender = ComponentMap[storyData.component];
                    const hasComment = storyData.data?.comments && storyData.data.comments.length > 0;
                    return (
                        <React.Fragment key={index}>
                            <div className={styles.storyWrapper} key={index}>
                                <div className={styles.littleStory} style={{ background: bgColor }} key={index}>
                                    <ComponentToRender storyData={storyData} story={{ view: 'readOnly' }} action={() => { }} />
                                </div>

                                <div className={styles.checkboxOverlay} onClick={(e) => handleStorySelection(e, index, storyData.component, storyData.index, hasComment)}>
                                    <div className={styles.parentCheckboxArea}>
                                        <input type="checkbox"
                                            checked={selectedStories?.some(selection => selection.index === index)}
                                            readOnly
                                        />
                                    </div>
                                    {hasComment && selectedStories?.some(selection => selection.index === index) &&
                                        <div className={styles.childCheckboxArea} onClick={(e) => handleChildClick(e, index)}>
                                            <input type="checkbox"
                                                checked={selectedStories?.some(selection => selection.index === index && selection.isCommentIncluded)}
                                                readOnly
                                            />
                                            <span className={styles.includeCommentText}>Include comment</span>
                                        </div>
                                    }
                                </div>
                            </div>

                            {showPeakValley && storyData.component === 'peakValley' &&
                                getPeakAndValleyForSelection()
                            }
                        </React.Fragment>
                    )
                }
                )}
            </div>
            <div className={styles.nextButtonContainer}>
                {selectedStories?.length > 0 ?
                    <button className={styles.nextButton} onClick={saveSelection}>
                        Next
                    </button>
                    : <p className={styles.noSelectionText}>No stories selected</p>
                }
            </div>
        </div>
    )
}

export default StorySelection;